import { StorageKeys } from './constants';

/**
 * Creates a storage wrapper with type-safe methods for getting, setting and removing values
 * @param storage - The Storage object (localStorage or sessionStorage)
 * @returns An object with typed methods for interacting with the storage
 * - set: Stores a value with automatic stringification
 * - get: Retrieves and parses a stored value
 * - remove: Removes a stored value
 */
const composeStorage = (storage: Storage) => ({
  /**
   * Sets a value in provided storage method with error handling
   * @param key - The key to store the value under
   * @param value - The value to store (will be stringified if not a string)
   */
  set: <T = any>(key: StorageKeys, value: T) => {
    const item = typeof value === 'string' ? value : JSON.stringify(value);

    try {
      storage.setItem(key, item);
    } catch (err) {
      // this error can occur in Firefox if the user are using an outdated version of the browser.
      if (err instanceof Error && err.name === 'NS_ERROR_FILE_CORRUPTED') {
        storage.clear();
      }
    }
  },

  /**
   * Gets a value from provided storage method with error handling
   * @param key - The key to retrieve the value for
   * @returns The parsed value or null if not found or error occurs (empty string will be returned as null)
   */
  get: <T = string>(key: StorageKeys): T | undefined => {
    const { [key]: item } = storage;

    if (!item) {
      return undefined;
    }

    try {
      return JSON.parse(item) as T;
    } catch {
      // if parsing fails, return the raw string value if it exists. This is needed for plain string values.
      return item ?? undefined;
    }
  },

  /**
   * Removes a value from provided storage method.
   * @param key - The key to remove
   */
  remove: (key: StorageKeys) => {
    storage.removeItem(key);
  },
});

/**
 * Local storage wrapper with type-safe methods for storing and retrieving data.
 */
export const LS = composeStorage(localStorage);

/**
 * Session storage wrapper with type-safe methods for storing and retrieving data.
 */
export const SS = composeStorage(sessionStorage);
